import React from "react";
import TopNav from "@/components/founding/TopNav";
import Footer from "@/components/founding/Footer";
import SignupModal from "@/components/founding/SignupModal";
import LegalDisclaimerModal from "@/components/founding/LegalDisclaimerModal";
import { useFoundingEntry } from "@/hooks/useFoundingEntry";

export default function TermsPage() {
    const { modalOpen, setModalOpen, authMode, setAuthMode, onSignIn, onSignUp } = useFoundingEntry();
    return (
        <main className="min-h-screen bg-black text-slate-100 flex flex-col">
            <TopNav onSignIn={onSignIn} onSignUp={onSignUp} />
            <div className="mx-auto w-full max-w-[1000px] px-6 py-20 flex-1">
                <div className="fsrs-overline mb-4 text-red-500">LAST UPDATED: OCTOBER 26, 2026</div>
                <h1 className="fsrs-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-10">
                    TERMS OF SERVICE
                </h1>
                
                <div className="prose prose-invert prose-red max-w-none space-y-8 fsrs-mono text-lg leading-relaxed">
                    <section>
                        <p className="text-slate-300">
                            Welcome to Fire Suppression Retrofit Studio (“FSRS”, “we”, “us”, or “our”). By accessing or using our platform at fsr.studio (the “Service”), you agree to these Terms of Service ("Terms"). If you do not agree, you may not use the Service.
                        </p>
                    </section>

                    <section className="border-l-4 border-red-600 pl-6 py-2 bg-red-600/5">
                        <h2 className="text-white font-bold text-xl mb-4">1. ACCEPTANCE OF TERMS</h2>
                        <p className="text-slate-300">
                            You must affirmatively accept these Terms before creating an account or completing any purchase. During account creation and checkout/upsell flows, you will see a clear checkbox: “I have read, understand, and agree to the Terms of Service and Engineering Disclaimer.” You cannot proceed without checking this box.
                        </p>
                    </section>

                    <section className="border-l-4 border-amber-500 pl-6 py-2 bg-amber-500/5">
                        <h2 className="text-amber-500 font-bold text-xl mb-4 uppercase">2. Engineering & Professional Disclaimer (Critical)</h2>
                        <p className="text-slate-300 font-bold">
                            FSRS is a rapid preliminary analysis tool to help licensed fire protection engineers quickly evaluate building drawings for RFP/bidding purposes. Outputs are NOT final construction documents or client deliverables.
                        </p>
                        <p className="text-slate-400 mt-4 italic">
                            All outputs are preliminary only and must be reviewed, modified, and stamped by a licensed Professional Engineer (PE) before any permitting, fabrication, installation, or client submittal. FSRS is not a substitute for professional engineering judgment.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-white font-bold text-xl mb-4 uppercase">3. Pricing and Future Adjustments</h2>
                        <p className="text-slate-300">
                            Founding Beta pricing is $249/month for the initial cohort. Each paid month includes 15 SRUs. Usage beyond that allowance is based on Standard Retrofit Units (SRUs). We reserve the right to adjust subscription fees, SRU rates, or other charges at any time.
                        </p>
                    </section>

                    <section>
                        <p className="text-slate-300">
                            <strong>Where your SRUs are stored.</strong> SRUs you buy are tied to the web browser you used to buy them, not to your account. They won't appear in a different browser or on a different device, and clearing your browser data may hide them. Please buy and use FSR Studio in the same browser. If your SRUs go missing, email support@fsr.studio. We may move SRUs to your account in the future and will update these Terms if we do.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-white font-bold text-xl mb-4 uppercase">4. Account Creation</h2>
                        <p className="text-slate-300">
                            You must provide accurate information when creating an account. You are responsible for maintaining the confidentiality of your login credentials.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-white font-bold text-xl mb-4 uppercase">5. Intellectual Property</h2>
                        <p className="text-slate-300">
                            All content generated by FSRS is for your internal, non-commercial evaluation use only (subject to PE review). You may not resell, redistribute, or use outputs for production without proper stamping.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-white font-bold text-xl mb-4 uppercase">6. Limitation of Liability</h2>
                        <p className="text-slate-300">
                            To the fullest extent permitted by law, FSRS and its providers shall not be liable for any indirect, incidental, special, consequential, or punitive damages. Our total liability shall not exceed the amount you paid in the last 12 months.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-white font-bold text-xl mb-4 uppercase">7. Governing Law</h2>
                        <p className="text-slate-300 text-xl font-bold">
                            These Terms are governed by the laws of the State of Iowa, USA.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-white font-bold text-xl mb-4">Do-Over Coupon (Iowa Beta Mailing)</h2>
                        <ol className="list-decimal space-y-4 pl-6 text-slate-300">
                            <li><strong>What it is.</strong> A do-over coupon lets you ask FSR Studio to re-run one project at no charge when the result doesn't meet your needs.</li>
                            <li><strong>Eligibility.</strong> Coupons are issued by mail to the firm named on the mailing, each with a unique number (IA-####). One coupon per firm. A do-over may be used on your OneFreeRun or on a paid run.</li>
                            <li><strong>When it can be used.</strong> A do-over is good for 10 days after signup or your first run.</li>
                            <li><strong>How to redeem.</strong> Email support@fsr.studio with your coupon number, your firm name, the run you'd like redone, and a short description of what missed. FSR Studio reviews the feedback and approves the do-over.</li>
                            <li><strong>Scope.</strong> A do-over re-runs the original project at the same scope (one drawing up to 15 MB). It has no cash value and is not transferable.</li>
                            <li><strong>Our discretion.</strong> FSR Studio may extend or grant additional do-overs at its discretion. That doesn't change these terms for anyone else.</li>
                            <li><strong>Changes.</strong> These terms may change for future mailings. Coupons already issued are honored under the terms printed on them.</li>
                        </ol>
                    </section>

                    <section className="pt-10">
                        <p className="text-slate-500 text-base">Questions? Contact support@fsr.studio</p>
                    </section>
                </div>
            </div>
            <Footer />
            <SignupModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                mode={authMode}
                onModeChange={setAuthMode}
            />
            <LegalDisclaimerModal />
        </main>
    );
}
